/**
 * Training Recommendation Reasons — the displayed basis, separated by who owns it.
 *
 * Pure, ranking-neutral helper that turns the numeric `ScoreBreakdown`
 * already produced by `trainingRelevanceScorer.scoreAndSort` into a short,
 * plain-English explanation shown under each artifact in the ticket Training
 * tab.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS MODULE WAS CHANGED — CORE-AH, 2026-09-09
 * ═══════════════════════════════════════════════════════════════════════════
 * CORE-AH's remaining named constitutional item is the explicit separation of
 * Core recommendation reasons from learned enrichment in the displayed
 * explanation. This module was where that separation failed.
 *
 * It held ONE priority list —
 *
 *     ['category', 'keyword', 'fitment', 'impact', 'engagement', 'pensieve']
 *
 * — and capped the output at two reasons. Pensieve is Catalyst inference and
 * sat LAST. So any two Core signals firing pushed it off the end: an artifact
 * could be lifted up to two points in the ranking by a learned signal and then
 * explain itself entirely in Core terms. The learned contribution influenced
 * the order and vanished from the account of it, which is the precise defect
 * the constitutional item names.
 *
 * The fix is a separation, NOT a new scorer. Ranking, sort order, thresholds
 * and every numeric value are untouched — they live in
 * `trainingRelevanceScorer.ts` and `useJobTrainingArtifacts.ts` and this module
 * still cannot move an artifact up or down. What changed is that the reasons
 * are now emitted in two classes that are rendered separately, and the
 * two-reason cap applies WITHIN the Core class only, so it can no longer hide
 * the fact that something learned contributed.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THE TWO CLASSES, AND WHY "CORE BASIS" RATHER THAN "FACT"
 * ═══════════════════════════════════════════════════════════════════════════
 * The tempting split is "deterministic facts" vs "AI guesses". It would be
 * wrong. `impact` and `engagement` are not raw observations — they are
 * derivations OptiBay computes from its own engagement data. Calling them
 * facts would overclaim in exactly the direction this item exists to prevent,
 * just one step further down.
 *
 * So the classes name OWNERSHIP, and the SP-05 class is preserved underneath
 * rather than flattened away:
 *
 *   CORE BASIS — OptiBay's own. Ticket/job/category match and vehicle fitment
 *   are direct matches against ticket and vehicle records; engagement and
 *   impact are Core-owned DERIVATIONS over OptiBay's own interaction data.
 *   Both belong to Core; only the first pair are observations.
 *
 *   LEARNED ENRICHMENT — Catalyst inference. Optional, and labelled as
 *   learned wherever it shows. Core must rank correctly without it (X-02:
 *   "Catalyst MUST NOT become a second authority"), so its absence is normal
 *   and produces no learned line at all rather than an empty one.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * `impact` IS A BLEND, AND ITS COMPOSITION IS CARRIED, NOT INFERRED
 * ═══════════════════════════════════════════════════════════════════════════
 * Found while tracing the path: `scoreImpact` in `trainingRelevanceScorer.ts`
 * adds up to +1 from TWO halves with DIFFERENT OWNERS —
 *
 *     +0.5  engagement.uniqueOpeners >= 3 AND completionCount >= 2   (Core)
 *     +0.5  pensieve.positiveSignals >= 3 AND avgConfidence > 0      (learned)
 *
 * — and collapses them into one number. A breakdown carrying `impact: 0.5`
 * does not say, on its face, which half fired. Presenting it unconditionally
 * as Core would let a purely learned contribution read as "proven helpful on
 * similar tickets", an OptiBay claim.
 *
 * THE FIRST ATTEMPT AT THIS INFERRED THE ANSWER AND WAS WRONG. It read: the
 * learned half needs `positiveSignals >= 3`, `scorePensieve` awards a point at
 * `positiveSignals >= 2`, therefore a learned half implies `pensieve > 0`,
 * therefore `pensieve === 0` proves impact is Core-owned. That implication is
 * true — but only in that direction. `scorePensieve` also awards a point for
 * `totalSamples >= 5 AND avgConfidence > 0` ALONE, so `pensieve > 0` does NOT
 * imply the learned half fired. A breakdown with 5 samples, 0.5 confidence and
 * ZERO positive signals carries `pensieve: 1` while `impact` came purely from
 * engagement depth. The inference would have labelled that Core-owned
 * derivation "learned-influenced" — suppressing a true Core reason and
 * printing a learned disclosure for something that never happened.
 *
 * So the composition is CARRIED instead. `scoreImpact` reports the split it
 * already computes as `impactCore` / `impactLearned` on the breakdown, and
 * this module reads them. They are provenance, not score:
 * `impactCore + impactLearned === impact`, neither is summed into `total`, and
 * ranking, ordering and every numeric value are unchanged.
 *
 * When the fields are ABSENT alongside a positive `impact` — a hand-built
 * breakdown from an older suite, never something `scoreAndSort` produces — the
 * composition was not recorded and this module says nothing about it. `impact`
 * appears in NEITHER class. Not knowing is a third state, and guessing at it
 * is what went wrong the first time.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT IS DELIBERATELY NOT SURFACED
 * ═══════════════════════════════════════════════════════════════════════════
 * `semantic` is a stub — `computeSemanticScore` returns 0 today and the scorer
 * calls it with an empty string. Surfacing it would present infrastructure that
 * does not exist as evidence about this ticket. It appears in NEITHER class,
 * and if it were ever the only positive contribution the artifact falls to the
 * total-positive fallback, which claims nothing specific.
 *
 * `getStyleAffinityBoost` is NOT in this path. It is imported only by
 * `useScheduleAwareRecommendations`, which serves the Passport
 * `ScheduleAwareRecommendations` surface; the ticket tab reaches its scores
 * through `useJobTrainingArtifacts` → `trainingRelevanceScorer.scoreAndSort`,
 * which never touches `divinationEngine`. Scope proven, left alone.
 *
 * Copy guidelines (unchanged):
 *   - Plain English, useful to a technician or advisor.
 *   - No internal vocabulary ("Pensieve", "semantic", "fitment boost",
 *     "scoreBreakdown", numeric values).
 *   - At most TWO Core reasons, joined with " and ", to keep the line
 *     scannable. The learned line is separate and is never capped away.
 */

import type { TrainingScoreBreakdown } from './training/scoring/types';

/**
 * Who owns a displayed reason.
 *
 * Deliberately NOT reusing `TrainingRecommendationReason['weightClass']` from
 * `@/types/trainingRecommendations` — that vocabulary
 * (`policy | strong | medium | weak | penalty | info`) grades how much a reason
 * WEIGHS, which is a different question from who PRODUCED it. A policy reason
 * and a learned reason can both be "strong".
 */
export type ReasonBasisClass = 'core' | 'learned';

/** Signals presented as Core basis. Priority order — first two win. */
type CoreSignal = 'category' | 'keyword' | 'fitment' | 'impact' | 'engagement';

/** Signals presented as learned enrichment. */
type LearnedSignal = 'pensieve' | 'impact_learned';

const CORE_PRIORITY: ReadonlyArray<CoreSignal> = [
  'category',
  'keyword',
  'fitment',
  'impact',
  'engagement',
];

/**
 * Learned signals in display order. `pensieve` leads because it is the direct
 * learned signal; `impact_learned` follows because it is a learned
 * contribution reaching the score through a blended field.
 *
 * This list is NOT capped. It has exactly two possible members and both are
 * disclosures the constitutional item requires; a cap here could only ever
 * suppress one of them, which is the defect being fixed.
 */
const LEARNED_ORDER: ReadonlyArray<LearnedSignal> = ['pensieve', 'impact_learned'];

/**
 * Plain-English fragment per signal. Framework-controlled — not
 * adapter-controlled, not LLM-derived — and therefore safe to render without
 * escaping.
 *
 * The learned fragments say "OptiBay has learned" rather than asserting
 * something about this vehicle or this ticket, because that is the honest
 * shape of an inference.
 */
const SIGNAL_REASON: Readonly<Record<CoreSignal | LearnedSignal, string>> = Object.freeze({
  category:       "matches this ticket's selected job",
  keyword:        'matches notes or symptoms on this ticket',
  fitment:        'fits this vehicle',
  impact:         'proven helpful on similar tickets',
  engagement:     'technicians engaged with this on similar work',
  pensieve:       'OptiBay has a learned signal for this job type',
  impact_learned: 'a learned signal also lifted its track record on similar tickets',
});

/**
 * Label for the learned line. Kept as an export so the UI and the tests agree
 * on one string, and so a copy review can find it.
 */
export const LEARNED_ENRICHMENT_LABEL = 'Learned enrichment (optional):';

/**
 * Fallback when `total` is positive but no individual signal cleared its
 * threshold — a low-noise rounding artifact, or a semantic-stub contribution
 * if that infrastructure ever lands. Claims nothing specific on purpose.
 */
const FALLBACK_TOTAL_POSITIVE = 'strong overall match for this ticket';

/**
 * Fallback when the breakdown is missing entirely or every signal is 0. The
 * ticket tab only renders artifacts that passed the YMME gate in
 * `useJobTrainingArtifacts`, so this is rare — but the UI is never blank.
 */
const FALLBACK_NEUTRAL = 'recommended for this ticket';

/** Maximum CORE reasons surfaced per artifact. Unchanged from Batch 18-B. */
const MAX_REASONS = 2;

/** A single displayed reason, carrying the class that produced it. */
export interface TrainingReasonPart {
  basis: ReasonBasisClass;
  text: string;
}

/**
 * The displayed explanation for one artifact, separated by class.
 *
 * `coreFragment` and `learnedFragment` are independent. A caller renders the
 * learned line whenever it is non-null, regardless of how many Core reasons
 * were shown — that independence is what stops the two-reason cap hiding a
 * learned contribution.
 */
export interface TrainingRecommendationExplanation {
  /** Core-owned reasons, priority-ordered, at most `MAX_REASONS`. */
  core: TrainingReasonPart[];
  /** Learned enrichment, never capped. Empty when Catalyst contributed nothing. */
  learned: TrainingReasonPart[];
  /**
   * The Core line, or `null` when nothing Core-owned fired and something
   * learned did. Null is meaningful: it is how a learned-only recommendation
   * avoids reading as a Core claim.
   */
  coreFragment: string | null;
  /** The learned line, or `null` when nothing learned contributed. */
  learnedFragment: string | null;
  /**
   * True when a learned signal contributed to the score the ranking used.
   * Equivalent to `learned.length > 0`; exposed so a caller can branch without
   * reaching into the array.
   */
  hasLearnedContribution: boolean;
}

/** A signal counts as firing only when strictly positive. */
function fired(breakdown: TrainingScoreBreakdown, key: keyof TrainingScoreBreakdown): boolean {
  return key in breakdown && (breakdown[key] ?? 0) > 0;
}

/**
 * The recorded composition of `impact`, or `null` when it was not recorded.
 *
 * `null` is a real answer and is treated as one: the caller shows `impact` in
 * neither class rather than guessing which half produced it.
 */
function impactComposition(
  breakdown: TrainingScoreBreakdown,
): { core: boolean; learned: boolean } | null {
  const hasCore = 'impactCore' in breakdown && breakdown.impactCore !== undefined;
  const hasLearned = 'impactLearned' in breakdown && breakdown.impactLearned !== undefined;
  if (!hasCore && !hasLearned) return null;
  return {
    core: (breakdown.impactCore ?? 0) > 0,
    learned: (breakdown.impactLearned ?? 0) > 0,
  };
}

/**
 * Build the separated explanation for a single artifact.
 *
 * Pure. Reads the breakdown the scorer already produced for the artifact being
 * displayed, and computes nothing the ranking did not already use.
 */
export function buildTrainingRecommendationExplanation(
  breakdown: TrainingScoreBreakdown | undefined,
): TrainingRecommendationExplanation {
  const empty: TrainingRecommendationExplanation = {
    core: [],
    learned: [],
    coreFragment: FALLBACK_NEUTRAL,
    learnedFragment: null,
    hasLearnedContribution: false,
  };

  if (!breakdown) return empty;

  // What the scorer recorded about how `impact` was composed. `null` means it
  // did not record — see the header; the honest response is silence, not a
  // guess.
  const composition = impactComposition(breakdown);

  // ── Core basis, priority-ordered and capped ────────────────────────────
  const core: TrainingReasonPart[] = [];
  for (const signal of CORE_PRIORITY) {
    if (core.length >= MAX_REASONS) break;
    if (!fired(breakdown, signal)) continue;
    // `impact` earns a Core reason only where a Core half was actually
    // recorded. Note this is independent of the learned half: a blend with
    // both halves firing states BOTH, in their own classes, because both
    // genuinely contributed.
    if (signal === 'impact' && !composition?.core) continue;
    core.push({ basis: 'core', text: SIGNAL_REASON[signal] });
  }

  // ── Learned enrichment, uncapped ───────────────────────────────────────
  const learned: TrainingReasonPart[] = [];
  for (const signal of LEARNED_ORDER) {
    const contributed =
      signal === 'pensieve'
        ? fired(breakdown, 'pensieve')
        : composition?.learned === true;
    if (contributed) learned.push({ basis: 'learned', text: SIGNAL_REASON[signal] });
  }

  const learnedFragment =
    learned.length > 0 ? learned.map((p) => p.text).join(' and ') : null;

  // ── The Core line ──────────────────────────────────────────────────────
  let coreFragment: string | null;
  if (core.length > 0) {
    coreFragment = core.map((p) => p.text).join(' and ');
  } else if (learned.length > 0) {
    // Learned-only. The Core line is omitted rather than filled with a
    // fallback, so nothing learned can be read as an OptiBay claim about this
    // ticket.
    coreFragment = null;
  } else if ((breakdown.total ?? 0) > 0) {
    coreFragment = FALLBACK_TOTAL_POSITIVE;
  } else {
    coreFragment = FALLBACK_NEUTRAL;
  }

  return {
    core,
    learned,
    coreFragment,
    learnedFragment,
    hasLearnedContribution: learned.length > 0,
  };
}

/**
 * The Core-basis line only.
 *
 * Retained at its original name and signature because existing call sites and
 * source-pin tests reference it. Its BEHAVIOUR changed in one respect, and
 * that change is the point of this slice: it no longer returns learned
 * enrichment. A caller that renders only this string is showing Core basis and
 * nothing else, which is now true of the string rather than merely hoped for.
 *
 * Callers that must disclose learned enrichment — the ticket Training tab does
 * — should use `buildTrainingRecommendationExplanation` and render both lines.
 */
export function buildTrainingRecommendationReason(
  breakdown: TrainingScoreBreakdown | undefined,
): string {
  const { coreFragment } = buildTrainingRecommendationExplanation(breakdown);
  // A learned-only artifact has no Core line. This function's contract is to
  // return a string, so it returns the neutral fallback — which claims nothing
  // — rather than borrowing the learned text.
  return coreFragment ?? FALLBACK_NEUTRAL;
}

/**
 * Convenience render-side prefix for the Core line. Surfaces wanting the
 * canonical lead-in use this rather than re-typing it.
 */
export const RECOMMENDATION_REASON_PREFIX = 'Recommended because:';

/**
 * Test-only re-exports. The tests assert the copy map is frozen (defensive
 * against a future developer reaching in to mutate it) and pin the exact
 * strings so a UI copy review can spot accidental drift.
 */
export const _SIGNAL_REASON_FOR_TESTS = SIGNAL_REASON;
export const _FALLBACK_TOTAL_POSITIVE_FOR_TESTS = FALLBACK_TOTAL_POSITIVE;
export const _FALLBACK_NEUTRAL_FOR_TESTS = FALLBACK_NEUTRAL;
export const _MAX_CORE_REASONS_FOR_TESTS = MAX_REASONS;
